/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';
import { MinorLevelColors } from './LevelField';
import { useWarningsTranslation } from '../../../utils/i18n';

export const MinorLevelChip = ({
  children,
  sx = [],
}: {
  children?: ReactNode;
  sx?: SxProps<Theme>;
}): ReactElement => {
  const { t } = useWarningsTranslation();
  const label = t('warning-level-minor');

  return (
    <Tooltip title={label}>
      <Box
        aria-label={label}
        component="span"
        role="img"
        sx={[
          {
            display: 'block',
            width: 40,
            height: '1.2em',
            textTransform: 'none',
            backgroundColor: MinorLevelColors.color,
            color: MinorLevelColors.textColor,
            border: 1,
            borderColor: MinorLevelColors.borderColor,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        tabIndex={0}
      >
        {children}
      </Box>
    </Tooltip>
  );
};
